<template>
  <div>
    <button
      class="btn"
      @click="toggleAudio"
    >
      <component
        :is="isPlaying ? IconPlayerPauseFilled : IconPlayerPlayFilled"
        :size="15"
        :class="isPlaying ? 'pause-icon' : 'play-icon'"
      />
    </button>
    <!-- Hidden audio element to manage playback -->
    <audio
      ref="audioRef"
      :src="props.audioUrl"
      preload="auto"
      @play="handlePlay"
      @pause="handlePause"
      @ended="handlePause"
    />
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch, onUnmounted, onMounted } from 'vue'
  import { IconPlayerPlayFilled, IconPlayerPauseFilled } from '@tabler/icons-vue'

  // Global registry to track all AudioPlaybackButton instances for exclusive playback
  const activeAudioInstances = new Set<() => void>()

  interface Props {
    audioUrl: string
    startTime: string
    endTime: string
  }

  const props = defineProps<Props>()

  const audioRef = ref<HTMLAudioElement | null>(null)
  const timeoutId = ref<number | null>(null)
  const isPlaying = ref(false)

  // Function to stop this instance's playback (called by other instances)
  const stopPlayback = () => {
    if (audioRef.value && isPlaying.value) {
      audioRef.value.pause()
      if (timeoutId.value) {
        clearTimeout(timeoutId.value)
        timeoutId.value = null
      }
    }
  }

  // Register this instance on mount
  onMounted(() => {
    activeAudioInstances.add(stopPlayback)
  })

  const toggleAudio = () => {
    if (!audioRef.value) return

    if (isPlaying.value) {
      audioRef.value.pause()
    } else {
      playAudio()
    }
  }

  const playAudio = async () => {
    if (!audioRef.value) return

    // Stop all other playing audio instances first
    activeAudioInstances.forEach((stop) => {
      if (stop !== stopPlayback) {
        stop()
      }
    })

    const startSeconds = Math.max(0, parseFloat(props.startTime) - 1)
    const endSeconds = parseFloat(props.endTime) + 1

    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }

    try {
      // Wait for audio to be ready if not already loaded
      if (audioRef.value.readyState < 2) {
        await new Promise<void>((resolve, reject) => {
          const audio = audioRef.value!
          const onCanPlay = () => {
            audio.removeEventListener('canplay', onCanPlay)
            audio.removeEventListener('error', onError)
            resolve()
          }
          const onError = () => {
            audio.removeEventListener('canplay', onCanPlay)
            audio.removeEventListener('error', onError)
            reject(new Error('Failed to load audio'))
          }
          audio.addEventListener('canplay', onCanPlay)
          audio.addEventListener('error', onError)
          audio.load()
        })
      }

      audioRef.value.currentTime = startSeconds
      await audioRef.value.play()

      const duration = (endSeconds - startSeconds) * 1000
      timeoutId.value = window.setTimeout(() => {
        audioRef.value?.pause()
      }, duration)
    } catch (error) {
      // Ignore AbortError - happens when play() is interrupted
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Audio playback error:', error)
      }
    }
  }

  const handlePlay = () => {
    isPlaying.value = true
  }

  const handlePause = () => {
    isPlaying.value = false
  }

  watch(
    () => props.audioUrl,
    (newUrl) => {
      if (audioRef.value) {
        audioRef.value.src = newUrl
      }
    }
  )

  onUnmounted(() => {
    // Remove from global registry
    activeAudioInstances.delete(stopPlayback)

    if (audioRef.value) {
      audioRef.value.pause()
      audioRef.value = null
    }
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }
  })
</script>

<style scoped>
  .play-icon {
    color: #2fb344;
  }

  .pause-icon {
    color: #d63939;
  }
</style>
